/**
 * RsDataPlots Component
 *
 * Plots view for routine surveillance data, with two modes driven by the
 * country selector:
 *
 * - "All Countries" (default): a metric dropdown appears; once a metric is
 *   chosen, one small scatter plot per country is rendered (small multiples).
 * - A specific country: the metric dropdown is hidden and one scatter plot per
 *   metric is rendered, all for the selected country.
 *
 * In both modes each plot has x = time (start_date), y = the parsed value, and
 * dot size = the number of days the point covers. Only national (ADMIN0) rows
 * are used — the same admin-level filtering the table applies per country.
 *
 * Rendered by RsDataInterface when the visual toggle is set to "plots".
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@/hooks/useAuth"
import {
  getAllRoutineSurveillanceData,
  getRoutineSurveillanceFacets,
  RoutineSurveillanceRow,
  RoutineSurveillanceTopic,
} from "@/services/routineSurveillanceService"
import { getIso3ToCountryMap } from "@/services/mapAdminInfoService"
import { Link } from "react-router-dom"
import { Maximize2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import SingleSelectCombobox, { ComboboxOption } from "@/components/shared/SingleSelectCombobox"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import ScatterPlot, { ScatterPoint } from "@/components/rs-data/ScatterPlot"
import DotSizeLegend from "@/components/rs-data/DotSizeLegend"
import {
  rowToScatterPoint,
  computeCategoryDifferences,
  computeAtypicalValues,
  parseRowDate,
  computeDaySpan,
  CATEGORY_COLUMNS,
  ALL_COUNTRIES_TOKEN,
  ALL_ADMIN_UNITS_TOKEN,
  AdminUnitOption,
  fetchAdminUnitOptions,
  fetchAdminUnitsForMetric,
  fetchMetricsForAdminUnit,
} from "@/components/rs-data/rsPlotData"
import CategoryDifferencesNote from "@/components/rs-data/CategoryDifferencesNote"
import AtypicalValuesNote from "@/components/rs-data/AtypicalValuesNote"
import CategoriesOkNote from "@/components/rs-data/CategoriesOkNote"

/**
 * Value used for the "All Countries" option in the country dropdown. Shared with
 * the URL layer (RsDataInterface) so the selection round-trips through the path.
 * When this is the selected value, the metric dropdown (and per-country grid) show.
 */
const ALL_COUNTRIES_VALUE = ALL_COUNTRIES_TOKEN

/**
 * The admin_level value of a national (country-aggregate) row. The plots only
 * ever show national data, so every fetch constrains admin_level to this value.
 * Sent as a string because filter values are serialised as strings (the backend
 * treats admin_level as a numeric column and renders it unquoted).
 */
const NATIONAL_ADMIN_LEVEL: string = "0"

/**
 * A selectable country in the country dropdown: its ISO3 code (used to scope
 * the data) paired with its display name.
 */
interface CountryOption {
  /** ISO3 code the data is scoped by. */
  iso3: string
  /** Human-readable country name shown in the dropdown. */
  name: string
}

/**
 * A single small-multiple plot's data: a stable key, a title shown above the
 * plot, and the points to plot. Used for both modes — in all-countries mode one
 * PlotSeries is a country; in single-country mode one PlotSeries is a metric.
 */
interface PlotSeries {
  /** Stable key for React and plot identity (ISO3 or metric name). */
  key: string
  /** Title shown above the plot (country name or metric name). */
  title: string
  /** The points (date on x, value on y, day-span on size) for this plot. */
  points: ScatterPoint[]
  /**
   * The source rows behind this plot's points (only rows that produced a
   * plotted point). Used to compute the "Category differences" note.
   */
  rows: RoutineSurveillanceRow[]
  /** ISO3 of the country this plot's data belongs to (for the expand link). */
  iso3: string
  /** metric_name this plot's data belongs to (for the expand link). */
  metric: string
  /**
   * (Subnational only) The map_admin_id of the admin unit this plot's data
   * belongs to, used to build the subnational expand link. Undefined on the
   * national view, where the expand link is keyed by country instead.
   */
  mapAdminId?: string
}

/** Props for the RsDataPlots component. */
interface RsDataPlotsProps {
  /**
   * Which surveillance level this view shows. National plots national (ADMIN0)
   * rows; subnational plots the chosen non-zero admin level and adds the Admin
   * Level + Admin Unit selectors. Defaults to "national".
   */
  level?: "national" | "subnational"
  /**
   * (Subnational) The selected non-zero admin level as its numeric string, or
   * "" when none is chosen. Ignored on the national view. Controlled by the
   * parent (from the URL).
   */
  selectedAdminLevel?: string
  /**
   * (Subnational) The selected admin unit's map_admin_id, or ALL_ADMIN_UNITS_TOKEN
   * ("all") for every unit at the chosen level. Ignored on the national view.
   * Controlled by the parent (from the URL).
   */
  selectedAdminUnit?: string
  /**
   * (Subnational) Called when the user picks a different admin level. The parent
   * updates the URL, which flows back down as selectedAdminLevel.
   *
   * @param nextAdminLevel - The chosen non-zero admin level (numeric string).
   */
  onAdminLevelChange?: (nextAdminLevel: string) => void
  /**
   * (Subnational) Called when the user picks a different admin unit. The parent
   * updates the URL, which flows back down as selectedAdminUnit.
   *
   * @param nextAdminUnit - The chosen unit's map_admin_id, or the "all" token.
   */
  onAdminUnitChange?: (nextAdminUnit: string) => void
  /**
   * The selected topic partition ("incidence" or "mortality"), controlled by
   * the parent (from the URL). Every fetch and the metric-options facet load are
   * scoped to this topic, so the plots only ever show one topic's data and the
   * metric dropdown only offers metrics that exist within it. Not owned here.
   */
  topic: RoutineSurveillanceTopic
  /**
   * The selected country, controlled by the parent (from the URL): an ISO3 code
   * or ALL_COUNTRIES_TOKEN ("all"). Drives which mode renders (all-countries vs
   * single-country). The view does not own this value.
   */
  selectedCountry: string
  /**
   * The selected metric_name, controlled by the parent (from the URL). Only used
   * in all-countries mode; "" when none is selected.
   */
  selectedMetric: string
  /**
   * Called when the user picks a different country. The parent updates the URL,
   * which flows back down as selectedCountry.
   *
   * @param nextCountry - The chosen country: an ISO3 code or ALL_COUNTRIES_TOKEN.
   */
  onCountryChange: (nextCountry: string) => void
  /**
   * Called when the user picks a different metric (all-countries mode). The
   * parent updates the URL, which flows back down as selectedMetric.
   *
   * @param nextMetric - The chosen metric_name ("" to clear).
   */
  onMetricChange: (nextMetric: string) => void
}

/**
 * Graph view for routine surveillance data.
 *
 * @param props - Component props.
 * @param props.level - Whether the interface is the National or Subnational
 *        view. Accepted for interface consistency; the graph plots national
 *        (ADMIN0) rows either way. Defaults to "national".
 * @returns The rendered graph-view controls and small-multiples grid.
 */
export default function RsDataPlots({
  level = "national",
  selectedAdminLevel = "",
  selectedAdminUnit = ALL_ADMIN_UNITS_TOKEN,
  topic,
  selectedCountry,
  selectedMetric,
  onCountryChange,
  onAdminLevelChange,
  onAdminUnitChange,
  onMetricChange,
}: RsDataPlotsProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  // Whether this is the subnational view — enables the Admin Level + Admin Unit
  // selectors and switches the plotted data from national (ADMIN0) rows to the
  // chosen non-zero level / unit.
  const isSubnational: boolean = level === "subnational"

  // Whether a specific admin unit (not "All Admin Units") is selected. Only
  // meaningful on subnational; drives the plots-mode branch (per-metric plots
  // for one unit vs per-unit plots for one metric).
  const hasSpecificAdminUnit: boolean =
    isSubnational &&
    selectedAdminUnit !== "" &&
    selectedAdminUnit !== ALL_ADMIN_UNITS_TOKEN

  // Country dropdown options, derived from the ISO3 -> countryName mapping
  // intersected with the ISO3 codes present in the RS snapshot, sorted
  // alphabetically, with an "All Countries" option prepended. The SELECTION is
  // controlled by the parent (URL); only the options are owned here.
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([])

  // Distinct `metric_name` values from the snapshot, loaded once. The selected
  // metric is controlled by the parent (URL).
  const [metricOptions, setMetricOptions] = useState<string[]>([])

  // (Subnational only) The non-zero admin levels available for the topic, as
  // numeric strings sorted ascending (national level 0 stripped). Loaded with
  // the other selector options.
  const [adminLevelOptions, setAdminLevelOptions] = useState<string[]>([])

  // (Subnational only) The admin units available for the selected country +
  // admin level, fetched on demand. "All Admin Units" is a synthetic option
  // prepended in the combobox, not stored here.
  const [adminUnitOptions, setAdminUnitOptions] = useState<AdminUnitOption[]>([])
  // Whether the admin unit options are currently being fetched, so the Admin
  // Unit dropdown can show a disabled state rather than a stale list.
  const [adminUnitsLoading, setAdminUnitsLoading] = useState<boolean>(false)

  // Whether the one-time initial load of the selector options (country/metric
  // facets and admin metadata) is still in flight. The page is held on a
  // loading spinner until this resolves, so the dropdowns are never shown empty
  // while their contents are still being fetched.
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true)

  // Set if the initial options load fails outright. Surfaced as a page-level
  // error (with retry) instead of silently leaving the dropdowns empty.
  const [optionsError, setOptionsError] = useState<string | null>(null)

  // The small-multiple plot series to render (one plot each). In all-countries
  // mode these are per-country; in single-country mode they are per-metric.
  const [series, setSeries] = useState<PlotSeries[]>([])
  const [chartLoading, setChartLoading] = useState<boolean>(false)
  const [chartError, setChartError] = useState<string | null>(null)

  // The selected [min, max] day-span range for the filter slider. Dots whose
  // day span falls outside this range are hidden. Initialised/reset to the full
  // extent ([0, globalMaxDaySpan]) whenever the data changes (see effect below),
  // so it starts showing everything.
  const [daySpanRange, setDaySpanRange] = useState<[number, number]>([0, 0])

  // When on (only in all-countries mode), every plot shares one x (time) and y
  // (value) axis range so the small multiples are directly comparable. Off by
  // default: each plot scales to its own data.
  const [uniformAxis, setUniformAxis] = useState<boolean>(false)

  // Monotonically increasing fetch ID used to discard stale chart responses:
  // if the selection changes while a fetch is in flight, the superseded result
  // is ignored rather than overwriting the newer selection's data.
  const chartFetchIdRef = useRef<number>(0)

  /**
   * Load the one-time selector options: the country dropdown options (ISO3
   * facet intersected with the country-name map), the distinct metric names,
   * and the ISO3 -> ADMIN0 id map used to constrain fetches to national rows.
   *
   * The page is held on a loading spinner (optionsLoading) until this resolves,
   * so the dropdowns are never shown empty while their contents are still being
   * fetched. On failure it sets optionsError so the page shows a retryable
   * error rather than empty, unusable dropdowns.
   *
   * @returns A promise that resolves once the options have loaded (or failed).
   */
  const loadSelectorOptions = useCallback(async (): Promise<void> => {
    setOptionsLoading(true)
    setOptionsError(null)

    try {
      const token = await getAccessToken()

      // Load the country name map, the ISO3 codes present in the snapshot,
      // and the distinct metric names in parallel. The dropdown should only
      // offer countries that have data, so we intersect the name map with the
      // snapshot's ISO3 facet. National rows are now identified by admin_level,
      // so the ISO3 -> ADMIN0 id map is no longer fetched.
      const [iso3ToCountry, iso3Facet, metricFacet, adminLevelFacet] =
        await Promise.all([
          getIso3ToCountryMap(),
          // These facets are scoped to the current topic: the country list only
          // offers countries with data in the topic, and the metric dropdown
          // only offers metrics that exist within it.
          getRoutineSurveillanceFacets(token, "iso3", topic),
          getRoutineSurveillanceFacets(token, "metric_name", topic),
          // Only needed for the subnational Admin Level dropdown; skip the extra
          // query on the national view.
          isSubnational
            ? getRoutineSurveillanceFacets(token, "admin_level", topic)
            : Promise.resolve(null),
        ])

      const availableIso3: Set<string> = new Set(iso3Facet.values)

      // Build the subnational admin-level options: every distinct admin_level
      // EXCEPT national (0), sorted numerically ascending. Empty on national.
      if (adminLevelFacet !== null) {
        const nonNationalLevels: string[] = adminLevelFacet.values
          .filter((levelValue) => levelValue !== "0")
          .sort((a, b) => Number(a) - Number(b))
        setAdminLevelOptions(nonNationalLevels)
      }

      // Build one option per ISO3 present in the data. If an ISO3 has no name
      // in the admin metadata, fall back to showing the raw code rather than
      // dropping it — otherwise a country with data would silently vanish.
      const options: CountryOption[] = Array.from(availableIso3)
        .map((iso3): CountryOption => ({
          iso3,
          name: iso3ToCountry.get(iso3) ?? iso3,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

      setCountryOptions(options)
      setMetricOptions(metricFacet.values)
    } catch (err) {
      // Surface the failure as a page-level error (with retry) rather than
      // silently leaving the dropdowns empty and unusable.
      setOptionsError(
        err instanceof Error ? err.message : "Failed to load selector options"
      )
    } finally {
      setOptionsLoading(false)
    }
  }, [getAccessToken, topic, isSubnational])

  // Load the selector options on mount and whenever the topic changes. The
  // options (country list + metric list) are topic-scoped, so a topic change
  // must refresh them. Keyed on loadSelectorOptions, which depends on topic.
  useEffect(() => {
    void loadSelectorOptions()
  }, [loadSelectorOptions])

  // The concrete ISO3 in effect, or null when nothing is selected. Subnational
  // has no "All Countries", so an empty selection resolves to null (not chosen).
  const activeIso3: string | null =
    selectedCountry === "" || selectedCountry === ALL_COUNTRIES_TOKEN
      ? null
      : selectedCountry

  // (Subnational only) Load the admin unit options whenever the country + admin
  // level selection resolves to a concrete pair. Cleared when either is missing
  // so a stale list is never shown. Guarded by a fetch id against races.
  const adminUnitsFetchIdRef = useRef<number>(0)
  useEffect(() => {
    if (!isSubnational || activeIso3 === null || selectedAdminLevel === "") {
      setAdminUnitOptions([])
      setAdminUnitsLoading(false)
      return
    }

    const currentFetchId: number = ++adminUnitsFetchIdRef.current
    setAdminUnitsLoading(true)

    void (async (): Promise<void> => {
      try {
        const token = await getAccessToken()
        const { units } = await fetchAdminUnitOptions(
          token,
          activeIso3,
          selectedAdminLevel,
          topic
        )
        if (currentFetchId !== adminUnitsFetchIdRef.current) return
        setAdminUnitOptions(units)
      } catch (err) {
        if (currentFetchId !== adminUnitsFetchIdRef.current) return
        console.error("Failed to load admin unit options:", err)
        setAdminUnitOptions([])
      } finally {
        if (currentFetchId === adminUnitsFetchIdRef.current) {
          setAdminUnitsLoading(false)
        }
      }
    })()
  }, [isSubnational, activeIso3, selectedAdminLevel, topic, getAccessToken])

  /**
   * Fetch every national row for the given metric across all countries and
   * build one scatter series per country (all-countries mode).
   *
   * National rows are selected directly by admin_level = 0 (the country
   * aggregate), so the query returns one country's national series per iso3 in
   * a single fetch. Rows whose date or value cannot be parsed are dropped
   * per-row so one bad row never voids a country.
   *
   * @param metric - The selected metric_name to plot.
   */
  const loadAllCountriesData = useCallback(async (metric: string): Promise<void> => {
    const currentFetchId: number = ++chartFetchIdRef.current
    setChartLoading(true)
    setChartError(null)

    try {
      const token = await getAccessToken()
      const bulk = await getAllRoutineSurveillanceData(token, {
        topic,
        filters: {
          metric_name: [metric],
          // National rows only, across every country: admin_level 0 is the
          // country aggregate. admin_level is numeric server-side; the value is
          // a numeric string.
          admin_level: [NATIONAL_ADMIN_LEVEL],
        },
        // Project the plotting fields plus iso3 (to group rows back to a country)
        // and the category columns (for the "Category differences" note).
        // end_date sizes each dot by day span.
        columns: [
          "iso3",
          "start_date",
          "end_date",
          "value",
          ...CATEGORY_COLUMNS.map(c => c.key),
        ],
      })

      // Discard the result if a newer selection superseded this fetch.
      if (currentFetchId !== chartFetchIdRef.current) {
        return
      }

      // The backend caps the bulk result; surface truncation rather than
      // silently plotting a partial set the user would read as complete.
      if (bulk.truncated) {
        setChartError(
          "Too many data points to display for this metric. Showing nothing " +
          "rather than a partial, misleading set — try a narrower metric."
        )
        setSeries([])
        return
      }

      // Group parsed points (and their source rows) by the row's own iso3.
      const pointsByIso3 = new Map<string, ScatterPoint[]>()
      const rowsByIso3 = new Map<string, RoutineSurveillanceRow[]>()
      for (const row of bulk.items) {
        const iso3: string | null = row.iso3
        // A row with no iso3 can't be assigned to a country plot; skip it.
        if (iso3 === null || iso3 === "") continue

        const point: ScatterPoint | null = rowToScatterPoint(row)
        if (point === null) continue

        const existingPoints: ScatterPoint[] | undefined = pointsByIso3.get(iso3)
        if (existingPoints === undefined) {
          pointsByIso3.set(iso3, [point])
          rowsByIso3.set(iso3, [row])
        } else {
          existingPoints.push(point)
          rowsByIso3.get(iso3)?.push(row)
        }
      }

      // Build one series per country that produced points, sorted by country
      // name so the grid ordering is stable and readable.
      const nextSeries: PlotSeries[] = Array.from(pointsByIso3.entries())
        .map(([iso3, points]): PlotSeries => ({
          key: iso3,
          title: countryOptions.find(c => c.iso3 === iso3)?.name ?? iso3,
          points,
          rows: rowsByIso3.get(iso3) ?? [],
          iso3,
          metric,
        }))
        .sort((a, b) => a.title.localeCompare(b.title))

      setSeries(nextSeries)
    } catch (err) {
      if (currentFetchId !== chartFetchIdRef.current) {
        return
      }
      setChartError(
        err instanceof Error ? err.message : "Failed to load graph data"
      )
      setSeries([])
    } finally {
      if (currentFetchId === chartFetchIdRef.current) {
        setChartLoading(false)
      }
    }
  }, [getAccessToken, countryOptions, topic])

  /**
   * Fetch every national row for a single country across all metrics and build
   * one scatter series per metric (single-country mode).
   *
   * The country's national rows are selected by filtering iso3 to the country
   * and admin_level to 0 (the national aggregate). Points are then grouped by
   * `metric_name`,
   * so each plot shows one metric's time series for the country.
   *
   * @param iso3 - The selected country's ISO3 code.
   */
  const loadSingleCountryData = useCallback(async (iso3: string): Promise<void> => {
    const currentFetchId: number = ++chartFetchIdRef.current
    setChartLoading(true)
    setChartError(null)

    try {
      const token = await getAccessToken()
      const bulk = await getAllRoutineSurveillanceData(token, {
        topic,
        filters: {
          // National rows for this one country: its iso3 at admin_level 0.
          iso3: [iso3],
          admin_level: [NATIONAL_ADMIN_LEVEL],
        },
        // metric_name is projected (instead of a fixed filter) so we can group
        // the country's rows into one plot per metric. The category columns are
        // projected too, for the "Category differences" note.
        columns: [
          "metric_name",
          "start_date",
          "end_date",
          "value",
          ...CATEGORY_COLUMNS.map(c => c.key),
        ],
      })

      if (currentFetchId !== chartFetchIdRef.current) {
        return
      }

      if (bulk.truncated) {
        setChartError(
          "Too many data points to display for this country. Showing nothing " +
          "rather than a partial, misleading set."
        )
        setSeries([])
        return
      }

      // Group parsed points (and their source rows) by metric_name.
      const pointsByMetric = new Map<string, ScatterPoint[]>()
      const rowsByMetric = new Map<string, RoutineSurveillanceRow[]>()
      for (const row of bulk.items) {
        const metric: string | null = row.metric_name
        // A row with no metric can't be assigned to a plot; skip it.
        if (metric === null || metric === "") continue

        const point: ScatterPoint | null = rowToScatterPoint(row)
        if (point === null) continue

        const existingPoints: ScatterPoint[] | undefined = pointsByMetric.get(metric)
        if (existingPoints === undefined) {
          pointsByMetric.set(metric, [point])
          rowsByMetric.set(metric, [row])
        } else {
          existingPoints.push(point)
          rowsByMetric.get(metric)?.push(row)
        }
      }

      // Build one series per metric that produced points, sorted by metric name.
      const nextSeries: PlotSeries[] = Array.from(pointsByMetric.entries())
        .map(([metric, points]): PlotSeries => ({
          key: metric,
          title: metric,
          points,
          rows: rowsByMetric.get(metric) ?? [],
          iso3,
          metric,
        }))
        .sort((a, b) => a.title.localeCompare(b.title))

      setSeries(nextSeries)
    } catch (err) {
      if (currentFetchId !== chartFetchIdRef.current) {
        return
      }
      setChartError(
        err instanceof Error ? err.message : "Failed to load graph data"
      )
      setSeries([])
    } finally {
      if (currentFetchId === chartFetchIdRef.current) {
        setChartLoading(false)
      }
    }
  }, [getAccessToken, topic])

  /**
   * (Subnational, All-Admin-Units mode) Fetch one country + admin level +
   * metric's rows and build one scatter series PER ADMIN UNIT. Mirrors the
   * national all-countries mode but grouped by admin unit within a single
   * country/level. Maps the shared SubnationalPlotSeries onto the local
   * PlotSeries shape (carrying map_admin_id for the expand link).
   *
   * @param iso3 - The selected country's ISO3 code.
   * @param adminLevel - The selected non-zero admin level (numeric string).
   * @param metric - The metric to plot across the country's admin units.
   */
  const loadAdminUnitsForMetric = useCallback(async (
    iso3: string,
    adminLevel: string,
    metric: string
  ): Promise<void> => {
    const currentFetchId: number = ++chartFetchIdRef.current
    setChartLoading(true)
    setChartError(null)

    try {
      const token = await getAccessToken()
      const { series: subSeries, truncated } = await fetchAdminUnitsForMetric(
        token,
        iso3,
        adminLevel,
        metric,
        topic
      )

      if (currentFetchId !== chartFetchIdRef.current) return

      if (truncated) {
        setChartError(
          "Too many data points to display for this metric. Showing nothing " +
          "rather than a partial, misleading set — try a narrower selection."
        )
        setSeries([])
        return
      }

      // Adapt the subnational series to the local PlotSeries shape. The plot
      // title is the admin unit name; iso3 + mapAdminId + metric back the
      // expand link.
      const nextSeries: PlotSeries[] = subSeries.map((s): PlotSeries => ({
        key: s.key,
        title: s.title,
        points: s.points,
        rows: s.rows,
        iso3,
        metric: s.metric,
        mapAdminId: s.mapAdminId,
      }))
      setSeries(nextSeries)
    } catch (err) {
      if (currentFetchId !== chartFetchIdRef.current) return
      setChartError(
        err instanceof Error ? err.message : "Failed to load graph data"
      )
      setSeries([])
    } finally {
      if (currentFetchId === chartFetchIdRef.current) {
        setChartLoading(false)
      }
    }
  }, [getAccessToken, topic])

  /**
   * (Subnational, specific-admin-unit mode) Fetch one admin unit's rows across
   * all metrics and build one scatter series PER METRIC. Mirrors the national
   * single-country mode but scoped to one admin unit. The unit's display name
   * is resolved from the loaded admin unit options for the plot title / expand
   * link.
   *
   * @param iso3 - The selected country's ISO3 code.
   * @param adminLevel - The selected non-zero admin level (numeric string).
   * @param mapAdminId - The specific admin unit's stable id.
   */
  const loadMetricsForAdminUnit = useCallback(async (
    iso3: string,
    adminLevel: string,
    mapAdminId: string
  ): Promise<void> => {
    const currentFetchId: number = ++chartFetchIdRef.current
    setChartLoading(true)
    setChartError(null)

    try {
      const token = await getAccessToken()
      // Resolve the unit's display name from the loaded options; fall back to
      // the id if it isn't found (e.g. options still loading) so the plot never
      // shows an empty title.
      const adminUnitName: string =
        adminUnitOptions.find((u) => u.mapAdminId === mapAdminId)?.name ??
        mapAdminId

      const { series: subSeries, truncated } = await fetchMetricsForAdminUnit(
        token,
        iso3,
        adminLevel,
        mapAdminId,
        adminUnitName,
        topic
      )

      if (currentFetchId !== chartFetchIdRef.current) return

      if (truncated) {
        setChartError(
          "Too many data points to display for this admin unit. Showing " +
          "nothing rather than a partial, misleading set."
        )
        setSeries([])
        return
      }

      // Adapt to the local PlotSeries shape. Each plot is one metric; the
      // expand link is keyed by this unit's mapAdminId + that metric.
      const nextSeries: PlotSeries[] = subSeries.map((s): PlotSeries => ({
        key: s.key,
        title: s.title,
        points: s.points,
        rows: s.rows,
        iso3,
        metric: s.metric,
        mapAdminId: s.mapAdminId,
      }))
      setSeries(nextSeries)
    } catch (err) {
      if (currentFetchId !== chartFetchIdRef.current) return
      setChartError(
        err instanceof Error ? err.message : "Failed to load graph data"
      )
      setSeries([])
    } finally {
      if (currentFetchId === chartFetchIdRef.current) {
        setChartLoading(false)
      }
    }
  }, [getAccessToken, topic, adminUnitOptions])

  // Whether "All Countries" is the active selection (national only). Drives
  // which national mode renders: the metric dropdown + per-country grid, or the
  // per-metric grid. Never true on subnational (no All Countries there).
  const isAllCountries: boolean =
    !isSubnational && selectedCountry === ALL_COUNTRIES_VALUE

  // Drive the correct loader from the current selection.
  //
  // National: All-countries mode waits for a metric; single-country mode fetches
  // immediately. Subnational: nothing loads until BOTH a country and an admin
  // level are chosen; then All-Admin-Units mode waits for a metric (per-unit
  // grid) while a specific unit fetches immediately (per-metric grid). Clearing
  // the relevant selection empties the grid.
  useEffect(() => {
    if (isSubnational) {
      // Gate on the required country + admin level.
      if (activeIso3 === null || selectedAdminLevel === "") {
        setSeries([])
        setChartError(null)
        return
      }
      if (hasSpecificAdminUnit) {
        // A specific unit: one plot per metric for that unit.
        void loadMetricsForAdminUnit(activeIso3, selectedAdminLevel, selectedAdminUnit)
        return
      }
      // All Admin Units: wait for a metric, then one plot per unit.
      if (selectedMetric === "") {
        setSeries([])
        setChartError(null)
        return
      }
      void loadAdminUnitsForMetric(activeIso3, selectedAdminLevel, selectedMetric)
      return
    }

    // National behaviour (unchanged).
    if (isAllCountries) {
      if (selectedMetric === "") {
        setSeries([])
        setChartError(null)
        return
      }
      void loadAllCountriesData(selectedMetric)
      return
    }
    // A specific country is selected.
    void loadSingleCountryData(selectedCountry)
  }, [
    isSubnational,
    activeIso3,
    selectedAdminLevel,
    selectedAdminUnit,
    hasSpecificAdminUnit,
    isAllCountries,
    selectedCountry,
    selectedMetric,
    loadAllCountriesData,
    loadSingleCountryData,
    loadAdminUnitsForMetric,
    loadMetricsForAdminUnit,
  ])

  // Map the country list into the {value,label} shape SingleSelectCombobox
  // expects, prepending the "All Countries" option so it sits first and is the
  // default selection.
  const countryComboboxOptions: ComboboxOption[] = useMemo(() => [
    // "All Countries" is offered on national only; subnational requires a
    // specific country.
    ...(isSubnational
      ? []
      : [{ value: ALL_COUNTRIES_VALUE, label: "All Countries" }]),
    ...countryOptions.map(country => ({
      value: country.iso3,
      label: country.name,
    })),
  ], [countryOptions, isSubnational])

  // Map the metric names into the {value,label} shape (value === label here,
  // since metric names are the stored value).
  const metricComboboxOptions: ComboboxOption[] = useMemo(
    () => metricOptions.map(metric => ({ value: metric, label: metric })),
    [metricOptions]
  )

  // (Subnational) Admin level options: value is the numeric level, label
  // prefixes it (e.g. "Level 1").
  const adminLevelComboboxOptions: ComboboxOption[] = useMemo(
    () => adminLevelOptions.map((levelValue) => ({
      value: levelValue,
      label: `Level ${levelValue}`,
    })),
    [adminLevelOptions]
  )

  // (Subnational) Admin unit options with the synthetic "All Admin Units"
  // default prepended. Value is the unit's map_admin_id; label is its name.
  const adminUnitComboboxOptions: ComboboxOption[] = useMemo(
    () => [
      { value: ALL_ADMIN_UNITS_TOKEN, label: "All Admin Units" },
      ...adminUnitOptions.map((unit) => ({
        value: unit.mapAdminId,
        label: unit.name,
      })),
    ],
    [adminUnitOptions]
  )

  /**
   * Report an admin level change up to the parent (subnational only). No-op
   * re-selection and empty values are ignored.
   *
   * @param nextAdminLevel - The chosen non-zero admin level (numeric string).
   */
  const handleAdminLevelChange = useCallback((nextAdminLevel: string): void => {
    if (nextAdminLevel !== "" && nextAdminLevel !== selectedAdminLevel) {
      onAdminLevelChange?.(nextAdminLevel)
    }
  }, [onAdminLevelChange, selectedAdminLevel])

  /**
   * Report an admin unit change up to the parent (subnational only). No-op
   * re-selection and empty values are ignored.
   *
   * @param nextAdminUnit - The chosen unit's map_admin_id, or the "all" token.
   */
  const handleAdminUnitChange = useCallback((nextAdminUnit: string): void => {
    if (nextAdminUnit !== "" && nextAdminUnit !== selectedAdminUnit) {
      onAdminUnitChange?.(nextAdminUnit)
    }
  }, [onAdminUnitChange, selectedAdminUnit])

  // The largest day-span (dot sizeValue) across every plot on the page. Passed
  // to each ScatterPlot so dot sizes share one scale and are comparable across
  // plots, rather than each plot scaling to its own maximum. Zero when no point
  // carries a size, in which case ScatterPlot falls back to its default radius.
  const globalMaxDaySpan: number = useMemo(() => {
    let maxSpan = 0
    for (const plotSeries of series) {
      for (const point of plotSeries.points) {
        if (point.sizeValue !== undefined && point.sizeValue > maxSpan) {
          maxSpan = point.sizeValue
        }
      }
    }
    return maxSpan
  }, [series])

  // Shared x (time) domain and y (value) max across every plot on the page,
  // used when "Uniform axis" is on so all small multiples share one scale.
  // Computed from the (unfiltered) series so the axes stay stable as the
  // day-span slider filters dots. Null domain when there are no dated points.
  const globalXDomain: [Date, Date] | null = useMemo(() => {
    let min: number | null = null
    let max: number | null = null
    for (const plotSeries of series) {
      for (const point of plotSeries.points) {
        const t: number = point.date.getTime()
        if (min === null || t < min) min = t
        if (max === null || t > max) max = t
      }
    }
    return min !== null && max !== null ? [new Date(min), new Date(max)] : null
  }, [series])

  const globalMaxValue: number = useMemo(() => {
    let maxVal = 0
    for (const plotSeries of series) {
      for (const point of plotSeries.points) {
        if (point.value > maxVal) maxVal = point.value
      }
    }
    return maxVal
  }, [series])

  // The smallest day-span across every plot on the page — the slider's min.
  // Null when no point carries a day span (nothing to bound), which disables the
  // slider. Computed separately from the max so the slider spans the actual data
  // range rather than starting at a hardcoded 0.
  const globalMinDaySpan: number | null = useMemo(() => {
    let minSpan: number | null = null
    for (const plotSeries of series) {
      for (const point of plotSeries.points) {
        if (point.sizeValue !== undefined) {
          if (minSpan === null || point.sizeValue < minSpan) {
            minSpan = point.sizeValue
          }
        }
      }
    }
    return minSpan
  }, [series])

  // Reset the day-span filter to the full extent (min..max of the data) whenever
  // the data changes, so a new metric/country starts by showing every dot.
  useEffect(() => {
    setDaySpanRange([globalMinDaySpan ?? 0, globalMaxDaySpan])
  }, [globalMinDaySpan, globalMaxDaySpan])

  // Whether the slider is at its full extent (no filtering). When true, points
  // that lack a day span (unknown end_date) are kept; narrowing either end hides
  // them, since an unknown span can't be placed within a bounded range.
  const isFullDaySpanRange: boolean =
    daySpanRange[0] <= (globalMinDaySpan ?? 0) && daySpanRange[1] >= globalMaxDaySpan

  // Apply the day-span filter to each series' points. Kept separate from the
  // fetched `series` so changing the slider re-filters without re-fetching.
  const filteredSeries: PlotSeries[] = useMemo(() => {
    // No filtering needed at full extent — return the series as-is.
    if (isFullDaySpanRange) {
      return series
    }
    const [minSpan, maxSpan] = daySpanRange
    // A point/row is in range when its day span is within [min, max]. Points and
    // rows are filtered by the same rule so the plot and its "Category
    // differences" note reflect exactly the same visible subset.
    const spanInRange = (span: number | undefined | null): boolean => {
      // Unknown span: only shown at full extent (handled above); excluded once
      // the range is narrowed.
      if (span === undefined || span === null) return false
      return span >= minSpan && span <= maxSpan
    }
    return series.map((plotSeries): PlotSeries => ({
      ...plotSeries,
      points: plotSeries.points.filter((point) => spanInRange(point.sizeValue)),
      rows: plotSeries.rows.filter((row) => {
        const start: Date | null = parseRowDate(row.start_date)
        const span: number | null =
          start !== null ? computeDaySpan(start, row.end_date) : null
        return spanInRange(span)
      }),
    }))
  }, [series, daySpanRange, isFullDaySpanRange])

  /**
   * Re-run the loader for the current selection. Used by the error-state retry.
   */
  const retryCurrentSelection = useCallback((): void => {
    if (isSubnational) {
      // Nothing to retry until the required country + level are present.
      if (activeIso3 === null || selectedAdminLevel === "") return
      if (hasSpecificAdminUnit) {
        void loadMetricsForAdminUnit(activeIso3, selectedAdminLevel, selectedAdminUnit)
        return
      }
      if (selectedMetric !== "") {
        void loadAdminUnitsForMetric(activeIso3, selectedAdminLevel, selectedMetric)
      }
      return
    }
    if (isAllCountries) {
      if (selectedMetric !== "") void loadAllCountriesData(selectedMetric)
      return
    }
    void loadSingleCountryData(selectedCountry)
  }, [
    isSubnational,
    activeIso3,
    selectedAdminLevel,
    selectedAdminUnit,
    hasSpecificAdminUnit,
    isAllCountries,
    selectedCountry,
    selectedMetric,
    loadAllCountriesData,
    loadSingleCountryData,
    loadAdminUnitsForMetric,
    loadMetricsForAdminUnit,
  ])

  // Hold the whole page on a loading spinner until the selector options have
  // been received, so the dropdowns are never shown empty/disabled while their
  // contents are still loading.
  if (optionsLoading) {
    return <LoadingSpinner loadingContent="Loading surveillance data..." />
  }

  // If the options failed to load the dropdowns would be empty and unusable, so
  // surface a retryable error for the whole page instead.
  if (optionsError !== null) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8">
        <p className="text-destructive">{optionsError}</p>
        <button
          onClick={() => { void loadSelectorOptions() }}
          className="text-sm underline text-primary hover:text-primary/80"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <>
      {/* Selector row: country combobox, plus the metric combobox only in
          all-countries mode. */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label
            htmlFor="rs-graph-country-select"
            className="text-sm text-muted-foreground whitespace-nowrap"
          >
            Country
          </label>
          <div className="w-[240px]">
            <SingleSelectCombobox
              id="rs-graph-country-select"
              options={countryComboboxOptions}
              value={selectedCountry}
              onChange={(next) => { if (next !== "") onCountryChange(next) }}
              placeholder="Select a country"
              emptyMessage="No countries found."
              disabled={countryOptions.length === 0}
              showClear={false}
            />
          </div>
        </div>

        {/* (Subnational only) Admin Level selector, just right of Country. */}
        {isSubnational && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="rs-graph-admin-level-select"
              className="text-sm text-muted-foreground whitespace-nowrap"
            >
              Admin Level
            </label>
            <div className="w-[150px]">
              <SingleSelectCombobox
                id="rs-graph-admin-level-select"
                options={adminLevelComboboxOptions}
                value={selectedAdminLevel}
                onChange={handleAdminLevelChange}
                placeholder="Select a level"
                emptyMessage="No admin levels found."
                // A country must be chosen before a level is meaningful.
                disabled={
                  activeIso3 === null || adminLevelComboboxOptions.length === 0
                }
                showClear={false}
              />
            </div>
          </div>
        )}

        {/* (Subnational only) Admin Unit selector, right of Admin Level. */}
        {isSubnational && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="rs-graph-admin-unit-select"
              className="text-sm text-muted-foreground whitespace-nowrap"
            >
              Admin Unit
            </label>
            <div className="w-[240px]">
              <SingleSelectCombobox
                id="rs-graph-admin-unit-select"
                options={adminUnitComboboxOptions}
                value={selectedAdminUnit}
                onChange={handleAdminUnitChange}
                placeholder="Select an admin unit"
                emptyMessage="No admin units found."
                // An admin level must be chosen (and its units loaded) first.
                disabled={selectedAdminLevel === "" || adminUnitsLoading}
                showClear={false}
              />
            </div>
          </div>
        )}

        {/* Metric selector + uniform-axis toggle. Shown in the "all units"
            aggregate modes: national All-Countries, or subnational
            All-Admin-Units (once a country + level are chosen). In both, one
            plot is rendered per unit for the chosen metric. Hidden when a
            specific country/admin unit is selected (those plot per-metric). */}
        {((!isSubnational && isAllCountries) ||
          (isSubnational &&
            !hasSpecificAdminUnit &&
            activeIso3 !== null &&
            selectedAdminLevel !== "")) && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="rs-graph-metric-select"
              className="text-sm text-muted-foreground whitespace-nowrap"
            >
              Metric
            </label>
            <div className="w-[240px]">
              <SingleSelectCombobox
                id="rs-graph-metric-select"
                options={metricComboboxOptions}
                value={selectedMetric}
                onChange={onMetricChange}
                placeholder="Select a metric"
                emptyMessage="No metrics found."
                disabled={metricOptions.length === 0}
                showClear={false}
              />
            </div>

            {/* Uniform-axis toggle: when on, every plot shares one x/y range. */}
            <div className="flex items-center gap-2">
              <Switch
                id="rs-plots-uniform-axis"
                checked={uniformAxis}
                onCheckedChange={setUniformAxis}
                aria-label="Uniform axis across all plots"
              />
              <label
                htmlFor="rs-plots-uniform-axis"
                className="text-sm text-muted-foreground whitespace-nowrap cursor-pointer"
              >
                Uniform axis
              </label>
            </div>
          </div>
        )}

        {/* Dot-size legend: explains that dot size encodes the number of days a
            point covers, drawing the min/max reference dots. Shown under the
            same condition as the day-span filter (dots actually vary in size),
            and pushed to the right so it groups with the filter. */}
        {globalMinDaySpan !== null && globalMinDaySpan < globalMaxDaySpan && (
          <div className="ml-auto">
            <DotSizeLegend
              minDaySpan={globalMinDaySpan}
              maxDaySpan={globalMaxDaySpan}
            />
          </div>
        )}

        {/* Day-span range filter. Filters visible dots to those whose day span
            (end_date - start_date) is within the selected range. Only shown when
            the data actually spans a range of day counts (min < max); a single
            day-span value has nothing to filter. */}
        {globalMinDaySpan !== null && globalMinDaySpan < globalMaxDaySpan && (
          <div className="flex items-center gap-3">
            <label
              htmlFor="rs-graph-dayspan-slider"
              className="text-sm text-muted-foreground whitespace-nowrap"
            >
              Days covered
            </label>
            <Slider
              id="rs-graph-dayspan-slider"
              className="w-[200px]"
              min={globalMinDaySpan ?? 0}
              max={globalMaxDaySpan}
              step={1}
              value={daySpanRange}
              onValueChange={(next) => {
                // Radix emits number[]; this is a two-thumb slider, so coerce to
                // the [min, max] tuple our state uses.
                if (next.length === 2) {
                  setDaySpanRange([next[0], next[1]])
                }
              }}
              aria-label="Filter dots by day span"
            />
            <span className="text-sm text-muted-foreground whitespace-nowrap tabular-nums">
              {daySpanRange[0].toLocaleString()}–{daySpanRange[1].toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Chart area. The grid renders once a selection resolves to data;
          intermediate states show guidance/loading/errors. */}
      <div className="flex-1 min-h-0 overflow-auto">
        {chartError !== null ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8">
            <p className="text-destructive">{chartError}</p>
            <button
              onClick={retryCurrentSelection}
              className="text-sm underline text-primary hover:text-primary/80"
            >
              Retry
            </button>
          </div>
        ) : isSubnational && (activeIso3 === null || selectedAdminLevel === "") ? (
          // Subnational gate: nothing plots until a country AND admin level are
          // chosen (mirrors the empty-metric prompt in national all-countries).
          <div className="flex items-center justify-center text-muted-foreground py-8">
            Select a country and admin level to view graphs.
          </div>
        ) : isSubnational && !hasSpecificAdminUnit && selectedMetric === "" ? (
          // Subnational All-Admin-Units mode: waits for a metric, then renders
          // one plot per admin unit.
          <div className="flex items-center justify-center text-muted-foreground py-8">
            Select a metric to view per-admin-unit graphs.
          </div>
        ) : !isSubnational && isAllCountries && selectedMetric === "" ? (
          <div className="flex items-center justify-center text-muted-foreground py-8">
            Select a metric to view per-country graphs.
          </div>
        ) : chartLoading ? (
          <LoadingSpinner loadingContent="Loading graph data..." />
        ) : series.length === 0 ? (
          <div className="flex items-center justify-center text-muted-foreground py-8">
            {isSubnational
              ? hasSpecificAdminUnit
                ? "No data available for this admin unit."
                : "No data available for this metric."
              : isAllCountries
                ? "No data available for this metric."
                : "No data available for this country."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSeries.map(plotSeries => (
              <div
                key={plotSeries.key}
                className="border border-border/50 rounded-md p-2"
              >
                <div className="flex items-center justify-between gap-1 mb-1">
                  <h3 className="text-sm font-medium truncate" title={plotSeries.title}>
                    {plotSeries.title}
                  </h3>
                  {/* Expand: open this single plot full-page in a new tab. The
                      full identity of the plot is encoded in the path so the
                      standalone page re-fetches exactly this series. National
                      keys it by country + metric; subnational needs the admin
                      level + admin unit (map_admin_id) + metric too, matching
                      the two different full-page-plot routes. */}
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    className="size-6 shrink-0 text-muted-foreground"
                  >
                    <Link
                      to={
                        (isSubnational
                          ? `/data/rs/subnational/full-page-plot/` +
                            `${encodeURIComponent(plotSeries.iso3)}/` +
                            `${encodeURIComponent(selectedAdminLevel)}/` +
                            // The unit's stable id; "" should never happen here
                            // (subnational series always carry mapAdminId).
                            `${encodeURIComponent(plotSeries.mapAdminId ?? "")}/` +
                            `${encodeURIComponent(plotSeries.metric)}`
                          : `/data/rs/national/full-page-plot/` +
                            `${encodeURIComponent(plotSeries.iso3)}/` +
                            `${encodeURIComponent(plotSeries.metric)}`) +
                        // Carry the topic so the full-page plot re-fetches the
                        // same topic's series, not the default.
                        `?topic=${encodeURIComponent(topic)}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${plotSeries.title} graph in a new tab`}
                    >
                      <Maximize2 className="size-3.5" />
                    </Link>
                  </Button>
                </div>
                <ScatterPlot
                  points={plotSeries.points}
                  maxSizeValue={globalMaxDaySpan}
                  xDomainOverride={
                    isAllCountries && uniformAxis && globalXDomain !== null
                      ? globalXDomain
                      : undefined
                  }
                  yMaxOverride={
                    isAllCountries && uniformAxis ? globalMaxValue : undefined
                  }
                />
                {/* Note under the plot, one of three states: (1) any category
                    column differs → warn; (2) uniform but some non-default
                    ("atypical") value → info; (3) uniform and all typical → an
                    all-clear tick. Mutually exclusive. */}
                {(() => {
                  const differingLabels: string[] = computeCategoryDifferences(plotSeries.rows)
                  if (differingLabels.length > 0) {
                    return (
                      <div className="mt-1">
                        <CategoryDifferencesNote differingLabels={differingLabels} />
                      </div>
                    )
                  }
                  const atypical = computeAtypicalValues(plotSeries.rows)
                  return (
                    <div className="mt-1">
                      {atypical.length > 0 ? (
                        <AtypicalValuesNote atypicalValues={atypical} />
                      ) : plotSeries.rows.length > 0 ? (
                        // Only show the all-clear tick when there are actually
                        // visible rows — an empty (fully filtered) plot isn't
                        // "uniform".
                        <CategoriesOkNote />
                      ) : null}
                    </div>
                  )
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
